import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api.js";

type ImportKind = "short_vertical" | "long_horizontal" | "square_feed" | "viral_final";

/**
 * Import an externally-produced clip (e.g. a Maestro render) as a keyword video
 * asset. Shared by the Studio tab and the Article tab's Maestro hand-off card so
 * the upload behaves identically wherever it appears. The role dropdown is kept
 * (pick each time) so the operator chooses short/long/square/final per import.
 */
export function ImportMaestroVideo({ keywordId, onImported }: { keywordId: string; onImported?: () => void }) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [kind, setKind] = useState<ImportKind>("short_vertical");

  const importVideo = useMutation({
    mutationFn: (file: File) => api.importKeywordVideo(keywordId, file, kind),
    onSuccess: () => {
      if (fileRef.current) fileRef.current.value = "";
      void qc.invalidateQueries({ queryKey: ["pipeline", "keyword", keywordId, "assets"] });
      onImported?.();
    },
  });

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <select
          className="input"
          value={kind}
          disabled={importVideo.isPending}
          onChange={(e) => setKind(e.target.value as ImportKind)}
        >
          <option value="short_vertical">Short vertical (TikTok / Reels / Shorts)</option>
          <option value="long_horizontal">Long horizontal (YouTube)</option>
          <option value="square_feed">Square (feed)</option>
          <option value="viral_final">Final (studio preview)</option>
        </select>
        <input
          ref={fileRef}
          type="file"
          accept="video/mp4,video/webm,video/quicktime"
          className="text-xs"
          disabled={importVideo.isPending}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) importVideo.mutate(f); }}
        />
        {importVideo.isPending && <span className="text-2xs text-muted">uploading…</span>}
      </div>
      {importVideo.isError && <p className="mt-2 text-2xs text-danger">{(importVideo.error as Error).message}</p>}
      {importVideo.isSuccess && !importVideo.isPending && (
        <p className="mt-2 text-2xs text-emerald-700">
          Imported ✓ — attached as a {kind.replace(/_/g, " ")} asset. It flows into distribution (and shows in Studio if you chose Final).
        </p>
      )}
    </div>
  );
}
